/* eslint-disable import/no-cycle */
/* eslint-disable max-len */
import { ModelType } from '@llws/platform-shared-types/types/db/auth';
import { RouteConfig } from 'vue-router';
import { ERouteMetaPermissionMode, IRouteConfigAuthMeta } from '@/shared';
import app from '@/main';

const inquiriesAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{
    key: 'lead_tracker',
    keyType: ModelType.building,
    mode: ERouteMetaPermissionMode.AT_LEAST_ONE,
  }],
};

const callTrackingAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{
    key: 'call_tracking',
    keyType: ModelType.building,
    mode: ERouteMetaPermissionMode.AT_LEAST_ONE,
  }],
};

const propertyIVRAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{
    key: 'interactive_voice_responses',
    keyType: ModelType.building,
    mode: ERouteMetaPermissionMode.AT_LEAST_ONE,
  }],
};

const callTracking = {
  path: '/leads/call-tracking',
  name: 'CallTracking',
  redirect: '/leads/call-tracking/logs',
  component: () => import('@/views/leads/call-tracking/logs/Index.vue'),
  meta: {
    auth: callTrackingAuth,
    title: 'Call Tracking',
    pageTitle: 'Call Tracking',
    icon: 'envelope',
    active: false,
  },
};

const inquiriesLog = {
  path: '/leads/inquiries',
  name: 'InquiriesLog',
  redirect: '/leads/inquiries/active',
  meta: {
    auth: inquiriesAuth,
    dynamicHideFromNav: () => {
      const vm = app();
      if (vm) {
        if (vm.$rsGlobals.$currentUser.isSuperUser) return false;

        return !vm.$rsGlobals.$featureActive('inquiries_log');
      }
      return true;
    },
    parent: null,
    title: 'Inquiries Log',
    active: false,
    infoUrl: '#',
    icon: 'inbox-in',
    noPagePadding: true,
    hideFromNav: false,
  },
};

const leadsInboxBeta = {
  path: '/leads',
  name: 'LeadsBetaInbox',
  redirect: inquiriesLog,
  component: () => import('@/views/leads/inbox/Index.vue'),
  meta: {
    auth: {
      ...inquiriesAuth,
      permissionRedirects: [
        {
          permission: 'call_tracking',
          redirectTo: callTracking,
        },
      ],
    },
    dynamicHideFromNav: () => {
      const vm = app();
      if (vm) {
        return vm.$rsGlobals.$featureActive('inquiries_log');
      }
      return false;
    },
    title: 'Leads Inbox (Beta)',
    icon: 'mail-bulk',
    active: false,
    hidePageTitle: true,
    noPagePadding: true,
    hideFromNav: false,
    badge: {
      variant: 'grey',
      text: 'BETA',
    },
  },
};

const activeInquiries = {
  path: '/leads/inquiries/active',
  name: 'ActiveInquiries',
  component: () => import('@/views/leads/inquiries/ActiveInquiries.vue'),
  meta: {
    auth: inquiriesAuth,
    parent: inquiriesLog,
    title: 'Active Inquiries',
    icon: 'inbox-in',
    infoUrl: '#',
    noPagePadding: true,
    hideFromNav: false,
    breadcrumbs: () => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/inquiries/active',
        title: 'Active Inquiries',
      },
    ]),
  },
};

const inactiveInquiries = {
  path: '/leads/inquiries/inactive',
  name: 'InactiveInquiries',
  component: () => import('@/views/leads/inquiries/InActiveInquiries.vue'),
  meta: {
    auth: inquiriesAuth,
    parent: inquiriesLog,
    title: 'Inactive Inquiries',
    icon: 'inbox-in',
    infoUrl: '#',
    noPagePadding: true,
    hideFromNav: false,
    breadcrumbs: () => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/inquiries/inactive',
        title: 'Inactive Inquiries',
      },
    ]),
  },
};

const activeLogSingle: RouteConfig = {
  path: '/leads/inquiries/active/:leadId/details',
  name: 'ActiveInquiriesLogSingle',
  component: () => import('@/views/leads/inquiries/single/Index.vue'),
  meta: {
    auth: inquiriesAuth,
    title: 'Details',
    active: false,
    hidePageTitle: true,
    hasSidebar: false,
    hideFromNav: true,
    infoUrl: '#',
    noPagePadding: true,
    parent: activeInquiries,
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/inquiries/active',
        title: 'Active Inquiries',
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const inactiveLogSingle: RouteConfig = {
  path: '/leads/inquiries/inactive/:leadId/details',
  name: 'InActiveInquiriesLogSingle',
  component: () => import('@/views/leads/inquiries/single/Index.vue'),
  meta: {
    auth: inquiriesAuth,
    title: 'Details',
    active: false,
    hidePageTitle: true,
    hasSidebar: false,
    hideFromNav: true,
    noPagePadding: true,
    infoUrl: '#',
    parent: inactiveInquiries,
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/inquiries/inactive',
        title: 'Inactive Inquiries',
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};
const propertyWaitingLists: RouteConfig = {
  path: '/leads/waiting-lists',
  name: 'PropertyWaitingLists',
  component: () => import('@/views/leads/waitinglists/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: [{
        key: 'waitinglist',
        keyType: ModelType.building,
        mode: ERouteMetaPermissionMode.AT_LEAST_ONE,
      }],
    },
    parent: null,
    title: 'Waiting Lists',
    active: false,
    icon: 'clipboard-list',
    noPagePadding: true,
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/waiting-lists',
        title: 'Waiting Lists',
      },
    ]),
  },
};

const callTrackingActiveNumberSummary: RouteConfig = {
  path: '/leads/call-tracking/active-number-summary',
  name: 'CallActiveNumberSummary',
  component: () => import('@/views/leads/call-tracking/active-numbers/Index.vue'),
  meta: {
    auth: callTrackingAuth,
    parent: callTracking,
    title: 'Active Number Summary',
    pageTitle: 'Call Active Number Summary',
    icon: 'phone',
    noPagePadding: true,
    active: false,
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/call-tracking/logs',
        title: 'Call Tracking',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const callTrackingMessageLogs: RouteConfig = {
  path: '/leads/call-tracking/message-logs',
  name: 'CallTrackingMessageLogs',
  component: () => import('@/views/leads/call-tracking/message-logs/Index.vue'),
  meta: {
    auth: callTrackingAuth,
    parent: callTracking,
    title: 'Text Messages',
    pageTitle: 'Text Messages',
    icon: 'phone',
    noPagePadding: true,
    active: false,
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/call-tracking/logs',
        title: 'Call Tracking',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const callTrackingLogs: RouteConfig = {
  path: '/leads/call-tracking/logs',
  name: 'CallTrackingLogs',
  component: () => import('@/views/leads/call-tracking/logs/Index.vue'),
  meta: {
    auth: callTrackingAuth,
    title: 'Call Logs',
    pageTitle: 'Call Logs',
    icon: 'phone',
    active: false,
    noPagePadding: true,
    parent: callTracking,
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/call-tracking/logs',
        title: 'Call Tracking',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const callTrackingCallForwarding: RouteConfig = {
  path: '/leads/call-tracking/call-forwarding',
  name: 'CallForwarding',
  component: () => import('@/views/leads/call-tracking/forwarding-numbers/Index.vue'),
  meta: {
    auth: callTrackingAuth,
    parent: callTracking,
    noPagePadding: true,
    title: 'Forwarding Numbers',
    pageTitle: 'Forwarding Numbers',
    icon: 'phone',
    active: false,
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/call-tracking/logs',
        title: 'Call Tracking',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const callTrackingUnattachedNumbers: RouteConfig = {
  path: '/leads/call-tracking/non-syndicated-numbers',
  name: 'CallNonSyndicatedNumbers',
  component: () => import('@/views/leads/call-tracking/non-syndicated-numbers/Index.vue'),
  meta: {
    auth: callTrackingAuth,
    parent: callTracking,
    noPagePadding: true,
    title: 'Non-Syndicated Numbers',
    pageTitle: 'Call Non-Syndicated Numbers',
    icon: 'phone',
    active: false,
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/call-tracking/logs',
        title: 'Call Tracking',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const callTrackingNumberSingle: RouteConfig = {
  path: '/leads/call-tracking/:callTrackingPhoneNumber/details',
  name: 'CallTrackingNumberDetails',
  component: () => import('@/views/leads/call-tracking/single/Index.vue'),
  meta: {
    auth: callTrackingAuth,
    parent: callTrackingActiveNumberSummary,
    title: 'Number Details',
    pageTitle: 'Call Tracking Number Details',
    icon: 'phone',
    active: false,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
    },
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/call-tracking/logs',
        title: 'Call Tracking',
      },
      {
        to: '/leads/call-tracking/active-number-summary',
        title: 'Active Number Summary',
      },
      {
        to: vm?.$route.path ?? '',
        title: 'Details',
      },
    ]),
  },
};

const callTrackingCallAndTextSettings: RouteConfig = {
  path: '/leads/call-tracking/call-and-text-settings',
  name: 'CallAndTextSettings',
  component: () => import('@/views/leads/call-tracking/settings/CallAndText.vue'),
  meta: {
    auth: callTrackingAuth,
    parent: callTracking,
    title: 'Call And Text Settings',
    pageTitle: 'Call And Text Settings',
    icon: 'cog',
    active: false,
    hideFromNav: false,
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/call-tracking/logs',
        title: 'Call Tracking',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
        disableTruncation: true,
      },
    ]),
    hasIframeBackButton: true,
  },
};

const callTrackingPropertyIVRSettings: RouteConfig = {
  path: '/leads/call-tracking/ivr/properties',
  name: 'PropertyIVRSettings',
  component: () => import('@/views/leads/call-tracking/settings/PropertyIVR.vue'),
  meta: {
    auth: propertyIVRAuth,
    parent: callTracking,
    noPagePadding: true,
    title: 'Property Interactive Voice Responses',
    icon: 'cog',
    active: false,
    hideFromNav: false,
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/call-tracking/logs',
        title: 'Call Tracking',
      },
      {
        to: '/leads/call-tracking/ivr/properties',
        title: 'Interactive Voice Responses',
        disableTruncation: true,
      },
      {
        to: vm?.$route.path ?? '',
        title: 'Properties',
        disableTruncation: true,
      },
    ]),
  },
};

const callTrackingPropertyIVRDetails: RouteConfig = {
  path: '/leads/call-tracking/ivr/properties/:buildingId/details',
  name: 'PropertyIVRDetails',
  component: () => import('@/views/leads/call-tracking/settings/single/PropertyIVRDetails.vue'),
  meta: {
    auth: propertyIVRAuth,
    parent: callTrackingPropertyIVRSettings,
    title: 'Details',
    active: false,
    icon: 'cog',
    footerBar: {
      enabled: true,
      hasSave: true,
    },
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/call-tracking/logs',
        title: 'Call Tracking',
      },
      {
        to: '/leads/call-tracking/ivr/properties',
        title: 'Interactive Voice Responses',
        disableTruncation: true,
      },
      {
        to: '/leads/call-tracking/ivr/properties',
        title: 'Properties',
      },
      {
        to: vm?.$route.path ?? '',
        title: 'Details',
      },
    ]),
  },
};

const callTrackingIVRCallGroups: RouteConfig = {
  path: '/leads/call-tracking/ivr/call-groups',
  name: 'IVRCallGroups',
  component: () => import('@/views/leads/call-tracking/settings/call-groups/Index.vue'),
  meta: {
    auth: propertyIVRAuth,
    parent: callTracking,
    title: 'Call Groups',
    active: false,
    icon: 'cog',
    footerBar: {
      enabled: true,
      hasSave: true,
    },
    breadcrumbs: (vm) => ([
      {
        to: '/leads/inquiries/active',
        title: 'Leads',
      },
      {
        to: '/leads/call-tracking/logs',
        title: 'Call Tracking',
      },
      {
        to: '/leads/call-tracking/ivr/properties',
        title: 'Interactive Voice Responses',
        disableTruncation: true,
      },
      {
        to: vm?.$route.path ?? '',
        title: 'Call Groups',
      },
    ]),
  },
};

export default [
  inquiriesLog,
  activeInquiries,
  inactiveInquiries,
  activeLogSingle,
  inactiveLogSingle,
  leadsInboxBeta,
  propertyWaitingLists,
  callTracking,
  callTrackingLogs,
  callTrackingMessageLogs,
  callTrackingActiveNumberSummary,
  callTrackingCallForwarding,
  callTrackingUnattachedNumbers,
  callTrackingNumberSingle,
  callTrackingCallAndTextSettings,
  callTrackingPropertyIVRSettings,
  callTrackingPropertyIVRDetails,
  callTrackingIVRCallGroups,
];
